/* eslint-disable react/jsx-no-target-blank */
import * as React from 'react';

import {
  IHighwayInfoSchema,
  IOfferSchema,
  IUndergroundInfoSchema,
} from '@cian/api-models/offer-card/v1/get-offer-data';
import { Link } from '../../common/components/link';
import { UndergroundIcon } from '../../common/components/underground_icon';
import { isArrayWithItems, isDefined, PREMIUM_REGIONS_IDS } from '../../utils';
import { getUndergroundTime, getConstructionInfo } from '../../utils/formatters';
import { getCommuteTypeByRegionId } from '../../utils/getCommuteTypeByRegionId';
import { scrollToElement } from '../../utils/navigation';
import { trackClickOnAddressItem, trackClickOnMapAnchor } from './tracking';

import * as styles from './index.css';
import { mergeStyles } from '@cian/utils';

const INTERPUNCT = '\u22C5';

export interface IGeoProps {
  offer: IOfferSchema;
  isMicrodataExperimentEnable: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export class Geo extends React.Component<IGeoProps, {}> {
  public render() {
    const { offer, isMicrodataExperimentEnable } = this.props;
    const { address, undergrounds, highways } = offer.geo;

    if (!isArrayWithItems(address)) {
      return null;
    }

    const regionId = address[0].id;

    const microData = isMicrodataExperimentEnable
      ? {
          itemScope: true,
          itemType: 'http://schema.org/Place',
        }
      : {};

    const addressString = address.map(addr => addr.shortName).join(', ');

    return (
      <div {...microData} className={styles['geo']}>
        <span {...{ itemProp: 'name', content: addressString }} />
        <address className={styles['address']}>
          {address.map((addr, index) => {
            const addressItem = (
              <Link
                key={`address_${index}`}
                className={styles['address-item']}
                href={addr.url}
                onClick={trackClickOnAddressItem}
              >
                {addr.shortName}
              </Link>
            );

            return [index > 0 && ', ', addressItem];
          })}
          {offer.status === 'published' && (
            <span className={styles['map-anchor']} onClick={this.handleClickOnMapAnchor}>
              На карте
            </span>
          )}
        </address>
        {undergrounds && (
          <ul className={styles['undergrounds']}>
            {undergrounds.map(underground => renderUnderground(underground, regionId))}
          </ul>
        )}
        {highways && (
          <ul className={styles['highways']}>{highways.map(highway => renderHighway(highway, regionId))}</ul>
        )}
      </div>
    );
  }

  private handleClickOnMapAnchor = () => {
    const map = document.getElementById('map');

    trackClickOnMapAnchor();

    if (map) {
      scrollToElement(map);
    }
  };
}

function renderHighway(highway: IHighwayInfoSchema, regionId: number) {
  return (
    <li className={styles['highway']} key={`highway_${highway.name}`}>
      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="11" viewBox="0 0 12 11">
        {/* tslint:disable-next-line:max-line-length */}
        <path
          fill="#757575"
          fillOpacity=".54"
          fillRule="evenodd"
          d="M10.037 1.506a.872.872 0 0 0-.83-.59H2.793a.877.877 0 0 0-.83.59L.75 5v4.667c0 .32.262.583.583.583h.584c.32 0 .583-.262.583-.583v-.584h7v.584c0 .32.262.583.583.583h.584c.32 0 .583-.262.583-.583V5l-1.213-3.494zM2.792 7.333a.874.874 0 1 1 0-1.75.874.874 0 1 1 0 1.75zm6.416 0a.874.874 0 1 1 0-1.75.874.874 0 1 1 0 1.75zm-7.29-2.916l.874-2.625h6.416l.875 2.625H1.917z"
        />
      </svg>
      <Link className={styles['highway_link']} href={highway.url} target="_blank">
        {`${highway.name} шоссе`}
      </Link>
      {highway.distance ? (
        <span>
          ,&nbsp;
          <span className={styles['highway_distance']}>
            {Math.round(highway.distance)} км от {getCommuteTypeByRegionId(regionId)}
          </span>
        </span>
      ) : null}
    </li>
  );
}

function renderUnderground(underground: IUndergroundInfoSchema, regionId: number) {
  const size = PREMIUM_REGIONS_IDS.includes(regionId) ? 17 : 10;
  const { underConstruction } = underground;
  const constructionInfo = getConstructionInfo(underground);

  return (
    <li className={styles['underground']} key={`underground_${underground.name}`}>
      <UndergroundIcon width={size} height={size} color={`#${underground.lineColor}`} regionId={regionId} />
      <a
        {...mergeStyles(styles['underground_link'], underConstruction && styles['under_construction'])}
        href={underground.url}
        target="_blank"
      >
        {underground.name}
      </a>
      {constructionInfo && <span className={styles['underground_time']}>{constructionInfo}</span>}
      {isDefined<number>(underground.travelTime) && underground.travelType && (
        <span className={styles['underground_time']}>
          {' '}
          {INTERPUNCT} {getUndergroundTime(underground)}
        </span>
      )}
    </li>
  );
}
